import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatsGrid } from '@/components/shared/StatsGrid';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import type { Pdf, Extraction, MonitoringLog, MonitoringStats } from '@/types';
import { formatDate, formatDateTime, formatFileSize } from '@/utils/helpers';
import { RefreshCw } from 'lucide-react';

interface MonitoringViewProps {
  pdfs: Pdf[];
  extractions: Extraction[];
  history: MonitoringLog[];
  stats: MonitoringStats | null;
  onCheckUpdates: () => void;
  onProcessPdf: (pdfId: number) => void;
  loading: boolean;
}

export function MonitoringView({
  pdfs,
  extractions,
  history,
  stats,
  onCheckUpdates,
  onProcessPdf,
  loading,
}: MonitoringViewProps) {
  const mockStats = stats
    ? {
        total_matches: stats.pdf_stats.total_pdfs,
        verified_matches: stats.pdf_stats.completed_pdfs,
        unmatched_fipe: stats.pdf_stats.processing_pdfs,
        unmatched_pbev: stats.pdf_stats.error_pdfs,
        avg_confidence: stats.extraction_stats.valid_extractions / stats.extraction_stats.total_extractions || 0,
        manual_matches: stats.extraction_stats.total_extractions,
      }
    : null;

  return (
    <div className="space-y-6">
      {/* Stats */}
      {mockStats && <StatsGrid stats={mockStats} />}

      {/* Controls */}
      <div className="flex justify-start">
        <Button onClick={onCheckUpdates} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Checking...' : 'Check for Updates'}
        </Button>
      </div>

      {/* PDFs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Tracked PDFs ({pdfs.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Filename</TableHead>
                  <TableHead>Download Date</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pdfs.map((pdf) => (
                  <TableRow key={pdf.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{pdf.filename}</div>
                        {pdf.error_message && (
                          <div className="text-xs text-red-500 mt-1">
                            {pdf.error_message}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{formatDate(pdf.download_date)}</TableCell>
                    <TableCell>{formatFileSize(pdf.file_size)}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          pdf.status === 'completed'
                            ? 'success'
                            : pdf.status === 'error'
                            ? 'destructive'
                            : 'secondary'
                        }
                      >
                        {pdf.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        onClick={() => onProcessPdf(pdf.id)}
                        disabled={loading || pdf.status === 'processing'}
                      >
                        Process
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Extractions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Extractions ({extractions.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>PDF</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Rows</TableHead>
                  <TableHead>CSV File</TableHead>
                  <TableHead>Validation</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {extractions.map((extraction) => (
                  <TableRow key={extraction.id}>
                    <TableCell>{extraction.pdf_filename}</TableCell>
                    <TableCell>{formatDateTime(extraction.extraction_date)}</TableCell>
                    <TableCell>{extraction.rows_extracted}</TableCell>
                    <TableCell>{extraction.csv_filename}</TableCell>
                    <TableCell>
                      <div>
                        <Badge
                          variant={
                            extraction.validation_status === 'valid'
                              ? 'success'
                              : 'destructive'
                          }
                        >
                          {extraction.validation_status}
                        </Badge>
                        {extraction.validation_errors && (
                          <div className="text-xs text-orange-500 mt-1 max-w-xs truncate">
                            {extraction.validation_errors.substring(0, 100)}...
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Monitoring History */}
      <Card>
        <CardHeader>
          <CardTitle>Monitoring History ({history.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Check Date</TableHead>
                  <TableHead>PDFs Found</TableHead>
                  <TableHead>New</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead>Errors</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>{formatDateTime(log.check_date)}</TableCell>
                    <TableCell>{log.pdfs_found}</TableCell>
                    <TableCell>
                      {log.new_pdfs > 0 ? (
                        <Badge variant="success">{log.new_pdfs}</Badge>
                      ) : (
                        log.new_pdfs
                      )}
                    </TableCell>
                    <TableCell>
                      {log.updated_pdfs > 0 ? (
                        <Badge variant="secondary">{log.updated_pdfs}</Badge>
                      ) : (
                        log.updated_pdfs
                      )}
                    </TableCell>
                    <TableCell>
                      {log.errors && (
                        <div className="text-xs text-red-500 max-w-xs truncate">
                          {log.errors.substring(0, 50)}...
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
